import { SUPER_ADMIN } from "@/components/data/constants";
import { isEntityScoped, SERVENGINE_SCOPE } from "@/store/entityTenantStore";

type SessionLevel = {
  userType?: string | null;
  isEntityLogin?: boolean | null;
};

type EntityContext = {
  isEntityLogin?: boolean | null;
  entityTenant?: string | null;
  isImpersonating?: boolean | null;
};

type AppLevelContext = {
  isAppLevel?: boolean | null;
  allEntities?: boolean | null;
  userType?: string | null;
};

type MergedViewContext = EntityContext & AppLevelContext;

/**
 * True when the session is at the OneEngine (main app) level rather than inside
 * one entity, so a list can hold rows from several entities and needs to name
 * the entity per row.
 *
 * Deliberately keyed on `isEntityLogin` alone, not on super-admin as well. Work
 * orders, upcoming visits, procurement, close-out and accounts payable all
 * widen their scope on exactly `isEntityLogin ? tenantId : undefined`, so a
 * non-super-admin main-app session (a service account, or a user attached to
 * default_tenant) receives rows from every tenant. Gating on super-admin too
 * would hide the column from precisely those sessions, which is the failure
 * this column exists to prevent.
 *
 * Two lists do NOT follow this rule and have their own predicates below:
 * proposals (`isSuperAdminPlatformView`) and reactive hire
 * (`isCrossTenantForSuperAdmin`). Each predicate mirrors one backend's actual
 * widening condition; do not collapse them.
 */
export function isPlatformView(session: SessionLevel): boolean {
  return !session.isEntityLogin;
}

/**
 * The reactive-hire exception. That queue widens on super-admin alone
 * (`reactiveHire/service.ts`: `tenantId: isSuperAdmin ? undefined : tenantId`),
 * ignoring `isEntityLogin`, so a super admin who has dropped into an entity
 * still receives every tenant's rows. Gating its Entity column on
 * `isPlatformView` would hide the column on exactly the mixed-entity list that
 * needs it.
 *
 * The backend asymmetry is tracked separately as a tenant-scoping defect; until
 * it is settled the column has to follow the data actually returned.
 */
export function isCrossTenantForSuperAdmin(session: SessionLevel): boolean {
  return session.userType === SUPER_ADMIN;
}

/**
 * Proposals only. That endpoint widens on super-admin AND non-entity
 * (`proposal/controller.ts`: `userType === SUPER_ADMIN && !isEntityLogin`),
 * which is stricter than every other list here.
 *
 * It needs its own predicate rather than borrowing `isPlatformView`, because on
 * Proposals the flag does not only add a column: it also builds the Entity
 * filter chip and triggers the entity dropdown fetch. A session the backend has
 * already scoped to one tenant would get an interactive filter that cannot
 * change its results, which is the same defect this ticket removes elsewhere.
 */
export function isSuperAdminPlatformView(session: SessionLevel): boolean {
  return session.userType === SUPER_ADMIN && !session.isEntityLogin;
}

/**
 * SERV-3226. True when the session is acting inside one entity by either route
 * in: an entity-subdomain login, or a main-app super admin who picked an entity
 * in the label menu. The two leave different traces (the id token's
 * `isEntityLogin` versus the persisted acting-entity scope that `axiosInstance`
 * sends as a header), and a screen that exists only at ServEngine level has to
 * close on both. `isPlatformView` cannot be reused here: it answers for list
 * widening, which the backend keys on the login flag alone.
 *
 * The acting scope is ignored while impersonating, because `axiosInstance` never
 * sends it alongside an impersonation and the backend serves the impersonated
 * user unscoped; the impersonated user's own `isEntityLogin` still counts.
 */
export function isInsideEntity(session: EntityContext): boolean {
  if (session.isEntityLogin) return true;
  if (session.isImpersonating) return false;
  return isEntityScoped(session.entityTenant ?? SERVENGINE_SCOPE);
}

/**
 * SERV-3232. A non-super-admin whose role may open the main domain and who reads
 * only the entities she belongs to. A `servengine_all` holder (`allEntities`)
 * reads unscoped like a super admin, so she keeps the full screens and is not
 * one. Once she picks an entity she works there as at its subdomain, so the
 * main-app-only screens open for her; a scoped super admin keeps them closed.
 */
export function isAppLevelUser(session: AppLevelContext): boolean {
  return !!session.isAppLevel && !session.allEntities && session.userType !== SUPER_ADMIN;
}

/**
 * SERV-3232. True for an app-level user who has not picked an entity: the merged
 * "ServEngine" view, where lists hold rows from every entity she belongs to.
 */
export function isMergedView(session: MergedViewContext): boolean {
  return isAppLevelUser(session) && !isInsideEntity(session);
}
